import React, { useEffect, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Table,
    Badge,
    UncontrolledTooltip
} from 'reactstrap';
import {
    FaUser,
    FaBuilding,
    FaPlus,
    FaTrash,
    FaSave,
    FaIdCard,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaMoneyBillWave,
    FaCreditCard,
    FaWallet,
    FaBusinessTime,
    FaUserTie,
    FaCheckCircle,
    FaEdit,
    FaEye
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import { Field } from 'formik';

const ClientForm = () => {
    const navigate = useNavigate();
    const params = useParams();

    // Client state
    const [client, setClient] = useState({
        nom: '',
        prenom: '',
        cin: '',
        telephone1: '',
        telephone2: '',
        email: '',
        adresse: '',
        ville: 'Casablanca',
        typeClient: 'PARTICULIER',
        codePostal: '',
        plafond: 0,
        chiffreAffaires: 0,
        entreprise: '', // New field for "Entreprise"
        ice: '',// New field for "ICE",
        active: true,
        solde: 0, // Ce champ sera en lecture seule
        commentaire: ''
    });

    const location = useLocation();

    // Handle client data loading for both module routes (with URL params) and legacy routes (with state)
    const clientId = params.id; // For module routes: /clients/:id/edit
    const clientEdit = location.state?.client || null; // For legacy routes
    console.log('clientEdit from state:', clientEdit);
    console.log('clientId from params:', clientId);
    useEffect(() => {
        // Load client data from state (legacy routes)
        if (clientEdit) {
            setClient({
                id: clientEdit.id || null,
                nom: clientEdit.nom || '',
                prenom: clientEdit.prenom || '',
                cin: clientEdit.cin || '',
                telephone1: clientEdit.telephone1 || '',
                telephone2: clientEdit.telephone2 || '',
                typeClient: clientEdit.typeClient || 'PARTICULIER',
                email: clientEdit.email || '',
                adresse: clientEdit.adresse || '',
                ville: clientEdit.ville || 'Casablanca',
                codePostal: clientEdit.codePostal || '',
                entreprise: clientEdit.entreprise || '', // New field for "Entreprise"
                active: clientEdit.active === true, // Ensure active is a boolean
                ice: clientEdit.ice || '',// New field for "ICE",
                plafond: clientEdit.plafond || 0,
                chiffreAffaires: clientEdit.chiffreAffaires || 0,
                solde: clientEdit.solde || 0, // Ce champ sera en lecture seule
                commentaire: clientEdit.commentaire || ''
            });

            setChantiers(clientEdit.chantiers || []); // Set initial chantiers from clientEdit
        }
        // Load client data by ID (module routes)
        else if (clientId) {
            // For module routes, we need to fetch the client data by ID
            // This would typically dispatch a Redux action to fetch client details
            console.log('Loading client data for ID:', clientId);
            // dispatch(clientActions.getById(clientId));
            // For now, we'll just log it - the actual implementation would depend on your API structure
        }
    }, [clientEdit, clientId]);
    // Chantiers state
    const [chantiers, setChantiers] = useState([]);
    const [currentChantier, setCurrentChantier] = useState({
        nom: '',
        type: 'Résidentiel',
        adresse: '',
        ville: 'Casablanca',
        montantTotal: '',
        dateDebut: '',
        dateFinEstimee: ''
    });

    // UI state
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState('client');
    const { t } = useTranslation();
    const dispatch = useDispatch(); // Assuming you are using Redux for state management
    const [showChantierSection, setShowChantierSection] = useState(false); // State to toggle chantier section

    // Villes marocaines
    const villesMaroc = [
        'Casablanca', 'Rabat', 'Khouribga', 'Marrakech', 'Fès', 'Tanger',
        'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan',
        'Safi', 'Mohammedia', 'El Jadida', 'Nador', 'Beni Mellal'
    ];

    // Types de chantiers
    const typesChantier = [
        'Résidentiel', 'Commercial', 'Industriel',
        'Public', 'Rénovation', 'Extension'
    ];

    // Handlers
    const handleClientChange = (e) => {
        const { name, value } = e.target;
        setClient((prev) => ({
            ...prev,
            [name]: name === 'active' ? value === 'true' : value, // Convert "true"/"false" to boolean for the active field
        }));
    };

    const handleChantierChange = (e) => {
        const { name, value } = e.target;
        setCurrentChantier(prev => ({ ...prev, [name]: value }));
    };

    const addChantier = () => {
        if (!currentChantier.nom || !currentChantier.adresse) {
            setErrors({ chantier: 'Veuillez remplir tous les champs obligatoires du chantier' });
            return;
        }

        setChantiers(prev => [...prev, currentChantier]);
        setCurrentChantier({
            nom: '',
            type: 'Résidentiel',
            adresse: '',
            ville: 'Casablanca',
            dateDebut: '',
            dateFinEstimee: ''
        });
        setErrors({});
        setActiveTab('chantiers-list');
    };

    const removeChantier = (index) => {
        const updatedChantiers = chantiers.filter((_, i) => i !== index);
        const nouveauSolde = client.creditMax -
            updatedChantiers.reduce((sum, c) => sum + parseFloat(c.montantTotal || 0), 0);

        setChantiers(updatedChantiers);
        setClient(prev => ({ ...prev, solde: nouveauSolde }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Format chantier dates to yyyy-MM-dd
        const formattedChantiers = chantiers.map((chantier) => ({
            ...chantier,
            dateDebut: chantier.dateDebut ? new Date(chantier.dateDebut).toISOString().split('T')[0] : null,
            dateFinEstimee: chantier.dateFinEstimee ? new Date(chantier.dateFinEstimee).toISOString().split('T')[0] : null,
        }));

        let payload = {
            data: {
                ...client,
                chantiers: formattedChantiers,
            },

            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: 'Succès',
                    text: 'Client enregistrés avec succès!',
                    confirmButtonText: 'OK',
                });

                setClient({
                    nom: '',
                    prenom: '',
                    cin: '',
                    telephone1: '',
                    telephone2: '',
                    typeClient: 'PARTICULIER',
                    email: '',
                    adresse: '',
                    ville: 'Casablanca',
                    codePostal: '',
                    plafond: 0,
                    entreprise: '',
                    ice: '',
                    solde: 0,
                    active: true,
                });
                setChantiers([]);
                // Navigate back to client list in the appropriate context
                if (location.pathname.startsWith('/sales-module')) {
                    navigate('/sales-module/clients');
                } else {
                    navigate(-1);
                }
            },
            onError: (error) => {
                Swal.fire("Erreur", error, "error");
            }
        };

        if (clientEdit) {
            payload = {
                ...payload,
                id: clientEdit.id,
            };

            dispatch(clientActions.updateClient(payload));
        } else {
            dispatch(clientActions.addClient(payload));
        }
    };

    // Formatage MAD
    const formatMAD = (montant) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
        }).format(montant);
    };

    return (
        <Container fluid>
            {/* Header Section */}
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                {clientEdit ? 'Modifier Client' : 'Nouveau Client'}
                            </h4>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Success Alert */}
            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert className="client-form-success-alert text-center">
                            <FaCheckCircle className="me-2" />
                            <strong>Client enregistré avec succès!</strong>
                            <p className="mb-0 mt-2">Les informations ont été sauvegardées dans le système.</p>
                        </Alert>
                    </Col>
                </Row>
            )}


            <Form onSubmit={handleSubmit} className="compact-form">
                <style>
                    {`
                        .compact-form .form-control-sm {
                            min-height: 30px;
                            font-size: 0.92rem;
                            padding: 0.25rem 0.5rem;
                        }
                        .compact-form .form-floating > .form-control-sm {
                            padding-top: 0.9rem;
                        }
                        .compact-form .form-floating > label {
                            padding-top: 0.3rem;
                            opacity: 0.65;
                            font-size: 0.92rem;
                        }
                        .compact-form .card-body {
                            padding: 1rem;
                        }
                        .compact-form .row,
                        .compact-form .client-form-row {
                            margin-bottom: 0.25rem !important;
                        }
                        .compact-form .form-group,
                        .compact-form .form-floating {
                            margin-bottom: 0.5rem !important;
                        }
                        .compact-form .mb-4 {
                            margin-bottom: 0.5rem !important;
                        }
                        .compact-form .g-3 {
                            --bs-gutter-y: 0.5rem;
                        }
                        .compact-form .btn {
                            padding: 0.35rem 0.75rem;
                            font-size: 0.92rem;
                        }
                        .compact-form .form-floating > .bg-light {
                            background-color: #f8f9fa !important;
                        }
                        .compact-form .form-floating > .bg-light:focus {
                            background-color: #ffffff !important;
                        }
                        .compact-form .client-type-buttons {
                            margin-bottom: 0.75rem !important;
                        }
                        .compact-form .client-form-actions {
                            margin-top: 0.5rem;
                        }
                    `}
                </style>
                {/* Client Information Tab */}
                {activeTab === 'client' && (
                    <Card className="client-form-card border-0 shadow-sm">
                        <CardBody className="client-form-section p-4">
                            <style>
                                {`
                                    .form-floating {
                                        margin-bottom: 1rem;
                                    }
                                    .form-floating > .form-control-sm {
                                        height: calc(2.5rem + 2px);
                                        padding: 0.5rem 0.75rem;
                                        font-size: 0.875rem;
                                        border-radius: 0.375rem;
                                    }
                                    .form-floating > label {
                                        padding: 0.5rem 0.75rem;
                                        font-size: 0.875rem;
                                    }
                                    .form-control-sm {
                                        transition: all 0.2s ease-in-out;
                                    }
                                    .form-control-sm:focus {
                                        background-color: #fff !important;
                                        box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
                                        border-color: #86b7fe !important;
                                    }
                                    .client-type-buttons {
                                        display: inline-flex;
                                        background: #f8f9fa;
                                        padding: 0.25rem;
                                        border-radius: 0.5rem;
                                        gap: 0.25rem;
                                        margin-bottom: 1.5rem;
                                    }
                                    .client-type-btn {
                                        border: none;
                                        background: transparent;
                                        padding: 0.5rem 1rem;
                                        border-radius: 0.375rem;
                                        display: flex;
                                        align-items: center;
                                        gap: 0.5rem;
                                        font-size: 0.875rem;
                                        color: #6c757d;
                                        transition: all 0.2s;
                                    }
                                    .client-type-btn.active {
                                        background: white;
                                        color: #0d6efd;
                                        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                                    }
                                    .client-type-btn:hover:not(.active) {
                                        background: rgba(255,255,255,0.5);
                                    }
                                    .required-field::after {
                                        content: ' *';
                                        color: #dc3545;
                                    }
                                    .g-3 {
                                        --bs-gutter-y: 1rem;
                                    }
                                `}
                            </style>
                            {/* Client Type Selection */}
                            <div className="client-type-toggle">
                                <div className="client-type-buttons">
                                    <button
                                        type="button"
                                        className={`client-type-btn ${client.typeClient === 'PARTICULIER' ? 'active' : ''}`}
                                        onClick={() => {
                                            handleClientChange({ target: { name: 'typeClient', value: 'PARTICULIER' } })
                                            handleClientChange({ target: { name: 'entreprise', value: '' } })
                                            handleClientChange({ target: { name: 'ice', value: '' } })
                                        }}
                                    >
                                        <FaUser />
                                        Particulier
                                    </button>
                                    <button
                                        type="button"
                                        className={`client-type-btn ${client.typeClient === 'ENTREPRISE' ? 'active' : ''}`}
                                        onClick={() => {
                                            handleClientChange({ target: { name: 'typeClient', value: 'ENTREPRISE' } })
                                            handleClientChange({ target: { name: 'prenom', value: '' } })
                                            handleClientChange({ target: { name: 'nom', value: '' } })
                                            handleClientChange({ target: { name: 'cin', value: '' } })
                                        }}
                                    >
                                        <FaBuilding />
                                        Entreprise
                                    </button>
                                </div>
                            </div>

                            {/* Particulier Fields */}
                            {client.typeClient === 'PARTICULIER' && (
                                <Row className="client-form-row">
                                    <Col md={4}>
                                        <FormGroup className="client-form-group">
                                            <Label for="nom" className="client-form-label required-field">
                                                <FaUser />
                                                Nom
                                            </Label>
                                            <Input
                                                type="text"
                                                id="nom"
                                                name="nom"
                                                onFocus={(e) => e.target.select()}
                                                value={client.nom}
                                                onChange={handleClientChange}
                                                className={`client-form-input ${errors.nom ? 'is-invalid' : ''}`}
                                                placeholder="El Fassi"
                                            />
                                            {errors.nom && <div className="client-form-error">{errors.nom}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup className="client-form-group">
                                            <Label for="prenom" className="client-form-label required-field">
                                                <FaUser />
                                                Prénom
                                            </Label>
                                            <Input
                                                type="text"
                                                id="prenom"
                                                name="prenom"
                                                onFocus={(e) => e.target.select()}
                                                value={client.prenom}
                                                onChange={handleClientChange}
                                                className={`client-form-input ${errors.prenom ? 'is-invalid' : ''}`}
                                                placeholder="Karim"
                                            />
                                            {errors.prenom && <div className="client-form-error">{errors.prenom}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup className="client-form-group">
                                            <Label for="cin" className="client-form-label">
                                                <FaIdCard />
                                                CIN
                                            </Label>
                                            <Input
                                                type="text"
                                                id="cin"
                                                name="cin"
                                                onFocus={(e) => e.target.select()}
                                                value={client.cin}
                                                onChange={handleClientChange}
                                                className="client-form-input"
                                                placeholder="AB123456"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                            {/* Entreprise Fields */}
                            {client.typeClient === 'ENTREPRISE' && (
                                <Row className="client-form-row">
                                    <Col md={6}>
                                        <FormGroup className="client-form-group">
                                            <Label for="entreprise" className="client-form-label required-field">
                                                <FaBuilding />
                                                Nom de l'entreprise
                                            </Label>
                                            <Input
                                                type="text"
                                                id="entreprise"
                                                name="entreprise"
                                                onFocus={(e) => e.target.select()}
                                                value={client.entreprise}
                                                onChange={handleClientChange}
                                                className={`client-form-input ${errors.entreprise ? 'is-invalid' : ''}`}
                                                placeholder="Nom de l'entreprise"
                                            />
                                            {errors.entreprise && <div className="client-form-error">{errors.entreprise}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup className="client-form-group">
                                            <Label for="ice" className="client-form-label required-field">
                                                <FaIdCard />
                                                Numéro ICE
                                            </Label>
                                            <Input
                                                type="text"
                                                id="ice"
                                                name="ice"
                                                onFocus={(e) => e.target.select()}
                                                value={client.ice}
                                                onChange={handleClientChange}
                                                className={`client-form-input ${errors.ice ? 'is-invalid' : ''}`}
                                                placeholder="000123456789012"
                                            />
                                            {errors.ice && <div className="client-form-error">{errors.ice}</div>}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                            <Row className="g-3">
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="telephone1" className="text-muted">
                                            <FaPhone className="me-2" size={12} />
                                            Téléphone (1) *
                                        </Label>
                                        <Input
                                            type="tel"
                                            id="telephone1"
                                            name="telephone1"
                                            onFocus={(e) => e.target.select()}
                                            value={client.telephone1}
                                            onChange={handleClientChange}
                                            invalid={!!errors.telephone1}
                                            placeholder="06 12 34 56 78"
                                        />

                                        {errors.telephone1 && <div className="text-danger small mt-1">{errors.telephone1}</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="telephone2" className="text-muted">
                                            <FaPhone className="me-2" size={12} />
                                            Téléphone (2) *
                                        </Label>
                                        <Input
                                            type="tel"
                                            id="telephone2"
                                            name="telephone2"
                                            onFocus={(e) => e.target.select()}
                                            value={client.telephone2}
                                            onChange={handleClientChange}
                                            invalid={!!errors.telephone2}
                                            placeholder="06 12 34 56 78"
                                        />

                                        {errors.telephone2 && <div className="text-danger small mt-1">{errors.telephone2}</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="email" className="text-muted">
                                            <FaEnvelope className="me-2" size={12} />
                                            Email
                                        </Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            onFocus={(e) => e.target.select()}
                                            value={client.email}
                                            onChange={handleClientChange}
                                            placeholder="k.elfassi@example.com"
                                        />

                                    </FormGroup>
                                </Col>


                            </Row>
                            <Row>
                                <Col md={12}>
                                    <FormGroup className="mb-4">
                                        <Label for="adresse" className="fw-bold">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            Adresse*
                                        </Label>
                                        <Input
                                            type="textarea"
                                            rows="2"
                                            id="adresse"
                                            name="adresse"
                                            onFocus={(e) => e.target.select()}
                                            value={client.adresse}
                                            onChange={handleClientChange}
                                            invalid={!!errors.adresse}
                                            placeholder="12 Rue Moulay Rachid"
                                            className="py-2"
                                        />
                                        {errors.adresse && <div className="text-danger small mt-1">{errors.adresse}</div>}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                        <Input
                                            type="textarea"
                                            name="commentaire"
                                            value={client.commentaire}
                                            rows="3"
                                            onChange={handleClientChange}
                                            className="form-control"
                                            placeholder={t('reglement.commentairePlaceholder')}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <div className="client-form-actions">
                                <Button
                                    type="submit"
                                    className="client-form-btn client-form-btn-success"
                                    id="saveClientBtn"
                                >
                                    <FaSave />
                                    Enregistrer
                                </Button>
                                <UncontrolledTooltip placement="top" target="saveClientBtn">
                                    Sauvegarder les informations du client
                                </UncontrolledTooltip>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Add Chantier Tab */}
                {activeTab === 'chantier' && (
                    <Card className="client-form-card">
                        <CardBody className="client-form-section">
                            <h3 className="client-form-section-title">
                                <FaBuilding />
                                Ajouter un Chantier
                            </h3>
                            {errors.chantier && (
                                <Alert className="client-form-error-alert">
                                    {errors.chantier}
                                </Alert>
                            )}
                            <Row>
                                <Col md={6}>
                                    <FormGroup className="mb-4">
                                        <Label for="nomChantier" className="fw-bold">
                                            Nom du chantier*
                                        </Label>
                                        <Input
                                            type="text"
                                            id="nomChantier"
                                            name="nom"
                                            value={currentChantier.nom}
                                            onChange={handleChantierChange}
                                            placeholder="Villa Al Mandari"
                                            className="py-2"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup className="mb-4">
                                        <Label for="typeChantier" className="fw-bold">
                                            Type de chantier
                                        </Label>
                                        <Input
                                            type="select"
                                            id="typeChantier"
                                            name="type"
                                            value={currentChantier.type}
                                            onChange={handleChantierChange}
                                            className="py-2"
                                        >
                                            {typesChantier.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup className="mb-4">
                                        <Label for="adresseChantier" className="fw-bold">
                                            Adresse du chantier*
                                        </Label>
                                        <Input
                                            type="text"
                                            id="adresseChantier"
                                            name="adresse"
                                            value={currentChantier.adresse}
                                            onChange={handleChantierChange}
                                            placeholder="Lotissement Al Mandari, Rue 12"
                                            className="py-2"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="villeChantier" className="fw-bold">
                                            Ville
                                        </Label>
                                        <Input
                                            type="select"
                                            id="villeChantier"
                                            name="ville"
                                            value={currentChantier.ville}
                                            onChange={handleChantierChange}
                                            className="py-2"
                                        >
                                            {villesMaroc.map(ville => (
                                                <option key={ville} value={ville}>{ville}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="dateDebut" className="fw-bold">
                                            <FaCalendarAlt className="me-2 text-muted" />
                                            Date ouverture chantier
                                        </Label>
                                        <Input
                                            type="date"
                                            id="dateDebut"
                                            name="dateDebut"
                                            value={currentChantier.dateDebut}
                                            onChange={handleChantierChange}
                                            className="py-2"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="dateFinEstimee" className="fw-bold">
                                            <FaCalendarAlt className="me-2 text-muted" />
                                            Date cloture chantier
                                        </Label>
                                        <Input
                                            type="date"
                                            id="dateFinEstimee"
                                            name="dateFinEstimee"
                                            value={currentChantier.dateFinEstimee}
                                            onChange={handleChantierChange}
                                            className="py-2"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-between mt-4">
                                <Button
                                    color="light"
                                    onClick={() => setActiveTab('client')}
                                    className="px-4"
                                >
                                    <FaUser className="me-2" />
                                    Retour
                                </Button>
                                <div>
                                    <Button
                                        color="primary"
                                        onClick={addChantier}
                                        className="px-4 me-2"
                                    >
                                        <FaPlus className="me-2" />
                                        Ajouter Chantier
                                    </Button>
                                    {chantiers.length > 0 && (
                                        <Button
                                            color="secondary"
                                            onClick={() => setActiveTab('chantiers-list')}
                                            className="px-4"
                                        >
                                            Voir Liste
                                        </Button>
                                    )}

                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}
                {/* Chantiers List Tab */}
                {activeTab === 'chantiers-list' && (
                    <Card className="border-0 shadow-sm">
                        <CardBody className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="text-primary m-0">
                                    <FaBuilding className="me-2" />
                                    Chantiers ({chantiers.length})
                                </h4>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => setActiveTab('chantier')}
                                >
                                    <FaPlus className="me-2" />
                                    Ajouter un autre
                                </Button>
                            </div>

                            {errors.chantiers && (
                                <Alert color="danger" className="mb-4">
                                    {errors.chantiers}
                                </Alert>
                            )}

                            <div className="table-responsive">
                                <Table hover className="mb-4">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Code</th>
                                            <th>Nom</th>
                                            <th>Type</th>
                                            <th>Adresse</th>
                                            <th>Dates</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chantiers.map((chantier, index) => (
                                            <tr key={index}>
                                                <td className="text-primary fw-bold">{chantier.code}</td>
                                                <td className="fw-bold">{chantier.nom}</td>
                                                <td>
                                                    <Badge color="info" pill>
                                                        {chantier.type}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <small>
                                                        {chantier.adresse}, {chantier.ville}
                                                    </small>
                                                </td>
                                                <td>
                                                    {chantier.dateDebut && (
                                                        <small>
                                                            {new Date(chantier.dateDebut).toLocaleDateString('fr-MA')}
                                                            {chantier.dateFinEstimee && ` - ${new Date(chantier.dateFinEstimee).toLocaleDateString('fr-MA')}`}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => removeChantier(index)}
                                                        outline
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex justify-content-between mt-4">
                                <Button
                                    color="light"
                                    onClick={() => setActiveTab('client')}
                                    className="px-4"
                                >
                                    <FaUser className="me-2" />
                                    Informations Client
                                </Button>
                                <Button
                                    color="success"
                                    type="submit"
                                    className="px-4"
                                >
                                    <FaSave className="me-2" />
                                    Enregistrer le Client
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </Form>

        </Container>
    );
};

export default ClientForm;